import { isNativeAudioCaptureAvailable } from "@/native/audioCapture";
import { NativeCaptureSource } from "@/native/nativeCaptureSource";
import { isRealNativePlatform } from "@/native/platform";
import { MicCapture, micCaptureIsSupported } from "./micCapture";

/**
 * Capture-source abstraction for the voice session: one PCM16LE mono
 * 24kHz chunk stream regardless of where the audio comes from.
 *
 * - `NativeCaptureSource` (src/native/) wraps the CohesiveAudioCapture
 *   plugin: AVAudioEngine, survives screen lock via the audio background mode.
 * - `WebMicCaptureSource` wraps the existing MicCapture worklet pipeline
 *   (simulator, web, or a native shell that predates the plugin).
 *
 * Both keep the capture *timeline* advancing while muted — muted chunks are
 * dropped, not delayed — so a pause is a truthful gap the ring buffer and the
 * server never hear.
 */

export interface CaptureChunk {
  pcm: ArrayBuffer;
  /** Milliseconds since capture start on the source's monotonic clock. */
  timestampMs: number;
  /** Bumps when the source restarted its engine (interruption recovery). */
  epoch: number;
}

export interface CaptureHandlers {
  onChunk: (chunk: CaptureChunk) => void;
  /** Native-only: AVAudioSession interruptions (calls, Siri, route loss). */
  onInterruption: (event: {
    phase: "began" | "ended";
    shouldResume: boolean;
    reason?: string;
  }) => void;
}

export interface CaptureSource {
  readonly kind: "native" | "web";
  start(handlers: CaptureHandlers): Promise<void>;
  stop(): Promise<void>;
  setMuted(muted: boolean): void;
  /** Bring capture back after an interruption tore the engine down. */
  resume(): Promise<void>;
}

/** 24kHz * 2 bytes — used to derive a chunk's duration from its byte length. */
export const PCM_BYTES_PER_MS = 48;

export function chunkDurationMs(chunk: CaptureChunk): number {
  return chunk.pcm.byteLength / PCM_BYTES_PER_MS;
}

class WebMicCaptureSource implements CaptureSource {
  readonly kind = "web" as const;
  private mic: MicCapture | null = null;
  private muted = false;
  /** Capture-timeline position derived from chunk count (20ms each). */
  private elapsedMs = 0;

  async start(handlers: CaptureHandlers): Promise<void> {
    this.mic = new MicCapture({
      onChunk: (chunk) => {
        const timestampMs = this.elapsedMs;
        // Advance the clock for muted chunks too — mute must be a gap in the
        // timeline, not a stall of it.
        this.elapsedMs += chunk.byteLength / PCM_BYTES_PER_MS;
        if (this.muted) return;
        handlers.onChunk({ pcm: chunk, timestampMs, epoch: 0 });
      },
    });
    await this.mic.start();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.mic?.setMuted(false); // keep chunks flowing; we gate + clock here
  }

  /** No-op: getUserMedia keeps running, so there is no engine to rebuild. */
  async resume(): Promise<void> {}

  async stop(): Promise<void> {
    const mic = this.mic;
    this.mic = null;
    if (mic) await mic.stop();
  }
}

/**
 * Prefer native capture (background-capable) and fall back to Web Audio.
 * Throws when neither is possible — callers surface "not supported".
 */
export async function createCaptureSource(): Promise<CaptureSource> {
  if (isRealNativePlatform() && (await isNativeAudioCaptureAvailable())) {
    return new NativeCaptureSource();
  }
  if (micCaptureIsSupported()) {
    return new WebMicCaptureSource();
  }
  throw new Error("Voice capture is not supported on this device.");
}
